import { useState, useEffect } from 'react'
import { Form, Button, Row, Col, Alert } from 'react-bootstrap'

const DocumentForm = ({ clienteId, document, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    tipo_documento: 'identificacion',
    descripcion: '',
    archivo: null,
    fecha_vencimiento: '',
    vigente: true
  })

  const [errors, setErrors] = useState({})

  // Valores válidos para tipo_documento (modelo Document)
  const TIPOS_DOCUMENTO = [
    { value: 'logo', label: 'Logo' },
    { value: 'identificacion', label: 'Identificación Oficial' },
    { value: 'comprobante_domicilio', label: 'Comprobante de Domicilio' },
    { value: 'acta_constitutiva', label: 'Acta Constitutiva' },
    { value: 'poder_notarial', label: 'Poder Notarial' },
    { value: 'cedula_fiscal', label: 'Cédula Fiscal' },
    { value: 'constancia_fiscal', label: 'Constancia de Situación Fiscal' },
    { value: 'contrato', label: 'Contrato de Servicios' },
    { value: 'otros', label: 'Otros' }
  ]

  useEffect(() => {
    if (document) {
      setFormData({
        tipo_documento: document.tipo_documento ?? document.tipo ?? 'identificacion',
        descripcion: document.descripcion ?? document.description ?? document.name ?? document.nombre ?? '',
        archivo: null,
        fecha_vencimiento: document.fecha_vencimiento ?? document.expiry_date ?? '',
        vigente: document.vigente !== undefined ? document.vigente : (document.valid !== undefined ? document.valid : true)
      })
    } else {
      setFormData({
        tipo_documento: 'identificacion',
        descripcion: '',
        archivo: null,
        fecha_vencimiento: '',
        vigente: true
      })
    }
  }, [document])

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : type === 'checkbox' ? checked : value
    }))
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const newErrors = {}
    if (!formData.tipo_documento) newErrors.tipo = 'Tipo es requerido'
    if (!formData.descripcion?.trim()) newErrors.descripcion = 'Descripción es requerida'
    if (!document && !formData.archivo) newErrors.archivo = 'El archivo es requerido para documentos nuevos'
    if (formData.tipo_documento === 'logo' && formData.archivo) {
      const name = formData.archivo.name || ''
      const ext = (name.split('.').pop() || '').toLowerCase()
      if (ext !== 'jpg' && ext !== 'jpeg' && ext !== 'png') {
        newErrors.archivo = 'El logo debe ser JPG, JPEG o PNG'
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    const desc = formData.descripcion.trim()
    const payload = {
      client: clienteId,
      document_type: formData.tipo_documento,
      name: desc,
      description: desc,
      expiry_date: formData.fecha_vencimiento || null,
      valid: formData.vigente
    }
    if (document?.id) payload.id = document.id
    
    if (formData.archivo) {
      const fd = new FormData()
      fd.append('client', clienteId)
      fd.append('document_type', formData.tipo_documento)
      fd.append('name', desc)
      fd.append('description', desc)
      fd.append('file', formData.archivo)
      if (formData.fecha_vencimiento) fd.append('expiry_date', formData.fecha_vencimiento)
      fd.append('valid', formData.vigente ? 'true' : 'false')
      if (document?.id) fd.append('id', document.id)
      onSubmit(fd)
    } else {
      onSubmit(payload)
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      {errors.submit && <Alert variant="danger">{errors.submit}</Alert>}
      
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Tipo de Documento *</Form.Label>
            <Form.Select
              name="tipo_documento"
              value={formData.tipo_documento}
              onChange={handleChange}
              isInvalid={!!errors.tipo}
              required
            >
              <option value="">Seleccionar...</option>
              {TIPOS_DOCUMENTO.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Form.Select>
            {errors.tipo && <Form.Control.Feedback type="invalid">{errors.tipo}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Fecha de Vencimiento</Form.Label>
            <Form.Control
              type="date"
              name="fecha_vencimiento"
              value={formData.fecha_vencimiento}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Descripción *</Form.Label>
            <Form.Control
              type="text"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              isInvalid={!!errors.descripcion}
              required
            />
            {errors.descripcion && <Form.Control.Feedback type="invalid">{errors.descripcion}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Archivo {!document && '*'}</Form.Label>
            <Form.Control
              type="file"
              name="archivo"
              onChange={handleChange}
              accept={formData.tipo_documento === 'logo' ? '.jpg,.jpeg,.png' : '.pdf,.jpg,.jpeg,.png,.doc,.docx'}
              isInvalid={!!errors.archivo}
            />
            {errors.archivo && <Form.Control.Feedback type="invalid">{errors.archivo}</Form.Control.Feedback>}
            <Form.Text className="text-muted">
              {formData.tipo_documento === 'logo'
                ? 'Solo JPG, JPEG o PNG (para logo en PDFs del cliente)'
                : 'Formatos permitidos: PDF, JPG, PNG, DOC, DOCX'}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Check
            type="checkbox"
            name="vigente"
            label="Documento Vigente"
            checked={formData.vigente}
            onChange={handleChange}
          />
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Guardando...' : (document ? 'Actualizar' : 'Crear')}
        </Button>
      </div>
    </Form>
  )
}

export default DocumentForm

